// RingsView: the welcome page's first screen as one sunburst (welcome/views.js),
// the share of the range's AI work as rings around a centre, in the board's
// own greys. The inner ring is the agents, each as far round as its share of
// the sessions, marked by its own colour as a line along the inside of its
// arc and beside its name; the ring outside it, each agent's kinds of work
// (or its models, as the caption's switch says), greys a step lighter the
// smaller they are; and round the rim, under each of those, a thin band of
// where review left its sessions: the share that met a standard in red, then
// the share flagged in amber, so where the work that needs a person sits
// reads around the rim before a word is read. The centre says how many sessions there are and how
// many met a standard or were flagged. Agents are named in their arcs where
// the name fits; outside the rim, every arc a standard was met in and the
// biggest are named, their leader lines never crossing. Arcs too thin to see
// fold into one per agent.
//
// An agent clicked narrows the search to it; an arc of the ring outside it,
// to the agent and that kind of work (or model) at once; a folded arc goes
// down the page to the section that lists them all. The centre's counts
// narrow to where review left the sessions.
import { useCallback, useMemo, useRef, useState } from "react";
import HoverTooltip from "../HoverTooltip.jsx";
import { FINDS, STEPS, agentName } from "../FlowBoard.jsx";
import { quoteValueIfNeeded } from "../../lib/queryLang.js";
import { reviewClause } from "../../lib/reviewClause.js";
import { holdsClause } from "../../lib/workspaceSearch.js";
import { EYEBROW_CLASS } from "../../lib/ui.js";
import { BodyState, Drill, FLAGGED, MET, NUM, ReviewReading, pctOf } from "./parts.jsx";

// Where review left a session that was flagged without meeting a standard;
// the centre's flagged count narrows to all of them at once.
const FLAGGED_PLACES = ["not_verified", "not_met", "set_aside"];
const IS_FLAGGED = new Set(FLAGGED_PLACES);
export const MET_CLAUSE = reviewClause("met");
export const FLAGGED_CLAUSE = reviewClause(FLAGGED_PLACES.join(","));
// The finder's models, to light a model it points at.
const MODEL_OF = FINDS.find((k) => k.field === "model")?.of;

// What the ring outside the agents shows, as the caption's switch names it:
// the clause an arc narrows to beside its agent's, what one and many of them
// are called, and the section that lists them all.
export const MIDDLES = [
  { id: "work", label: "Work", one: "kind of work", many: "kinds of work", part: "work", clause: (v) => STEPS.topic.clause(v) },
  { id: "models", label: "Models", one: "model", many: "models", part: "models", clause: (v) => `model:${quoteValueIfNeeded(v)}` },
];
export const MIDDLE_KEY = "origin-welcome-rings-middle-v1";
function readMiddle() {
  try {
    const saved = globalThis.sessionStorage?.getItem(MIDDLE_KEY);
    return MIDDLES.some((m) => m.id === saved) ? saved : MIDDLES[0].id;
  } catch {
    return MIDDLES[0].id;
  }
}
function keepMiddle(id) {
  try {
    globalThis.sessionStorage?.setItem(MIDDLE_KEY, id);
  } catch {
    // Not kept past this page: the choice still stands while it is open.
  }
}

// The picture's geometry, in px. The caption line over it; the ground
// between the rings; how thin an arc may be before it is folded, in degrees;
// the centre's room for what it says; how far a share that met a standard
// stands out past the rim, so the thinnest still catches the eye.
export const CAP_H = 26;
const RING_GAP = 3;
export const FOLD_DEG = 1.5;
const HOLE_MIN = 70;
const SPIKE = 7;
const HALO_MIN = 12;
// A label outside the rim: its name's line and the line under it, the room
// between labels, how far past the rim its leader starts and bends, how
// clear of the rim it stays and how far from it it starts, how wide it may
// be, and how far it may stand from its arc.
const NAME_H = 16;
const STATS_H = 14;
const LABEL_H = NAME_H + STATS_H;
const LABEL_H2 = 2 * NAME_H + STATS_H;
const LABEL_GAP = 4;
const LEAD_FROM = 2;
const ELBOW = 8;
const CLEAR = 3;
const LABEL_OFF = 16;
const LABEL_MIN = 84;
const LABEL_MAX = 200;
const shiftOf = (R) => Math.max(60, R * 0.5);
// How many labels the room holds at most, so the picture stays a picture
// however many arcs a standard was met in: those where most were met first.
const labelsFor = (chartH) => Math.max(4, Math.floor(chartH / 42));
const BIGGEST = 4;
// What the swatch before a label's name takes, with its gap.
const SWATCH_W = 14;

// The ring's greys: a step for each of an agent's biggest arcs, the biggest
// darkest, the rest one step; what is folded fainter, what is not read yet
// fainter still. A pointed arc goes a step up. The agents' ring is a grey a
// step stronger than any of them.
const grey = (p) => `color-mix(in srgb, var(--muted) ${p}%, var(--workspace-bg))`;
const STEP_P = [17, 13, 10, 8];
const FOLD_P = 6;
const UNREAD_P = 4;
const LIFT_P = 9;
const AGENT_P = 24;
const AGENT_LIT_P = 32;
// An agent's own colour, as the board marks one: a line along the inside of
// its arc and a square beside its name, so the rim's red stays the only
// colour that stands out.
const EDGE_W = 3;
const hueOf = (hue) => hue || "var(--muted)";
// Flagged is softer than met on the rim, so a standard met leads.
const FLAGGED_BAND = `color-mix(in srgb, ${FLAGGED} 62%, var(--workspace-bg))`;
// What is not pointed at, while something is, stands back this far.
const FADED = 0.55;


const agentLabel = (tag) => (tag ? agentName(tag) : "No agent recorded");
// A folded arc's name: how many more there are, or how many in all where
// every one of an agent's was too thin to show.
const foldName = (fold, many) => `${NUM.format(fold.folded.length)} ${fold.alone ? "" : "more "}${many}`;
const plural = (n, one, many) => `${NUM.format(n)} ${n === 1 ? one : many}`;
const byCount = (a, b) => b[1].count - a[1].count || String(a[0]).localeCompare(String(b[0]));

// About how wide some text is, a little over rather than under, for what
// fits where: capitals and digits are wider than most letters.
const charW = (ch) => (/[MWmw@]/.test(ch) ? 0.84 : /[A-Z]/.test(ch) ? 0.7 : /[0-9]/.test(ch) ? 0.62 : /[\s.,:;'|!ilj]/.test(ch) ? 0.3 : 0.56);
const textW = (s, px = 11) => {
  let em = 0;
  for (const ch of String(s)) em += charW(ch);
  return Math.ceil(em * px);
};
// A caption's words, in capitals and spaced a little.
const capW = (s) => textW(String(s).toUpperCase(), 11) + Math.ceil(String(s).length * 0.66);

function tallyIn(m, key, met, flagged) {
  let e = m.get(key);
  if (!e) {
    e = { count: 0, met: 0, flagged: 0 };
    m.set(key, e);
  }
  e.count += 1;
  e.met += met;
  e.flagged += flagged;
}

// One pass over the sessions: each agent's sessions, where review left them,
// and its kinds of work and its models the same way; the agents the busiest
// first.
export function ringsOf(sessions) {
  const by = new Map();
  let met = 0;
  let flagged = 0;
  for (const s of sessions) {
    const tag = s.agent || "";
    let a = by.get(tag);
    if (!a) {
      a = { tag, count: 0, met: 0, flagged: 0, work: new Map(), models: new Map() };
      by.set(tag, a);
    }
    const m = s.outcome === "met" ? 1 : 0;
    const f = IS_FLAGGED.has(s.outcome) ? 1 : 0;
    a.count += 1;
    a.met += m;
    a.flagged += f;
    met += m;
    flagged += f;
    tallyIn(a.work, s.topic || "", m, f);
    tallyIn(a.models, s.model || "", m, f);
  }
  const agents = [...by.values()].sort((x, y) => y.count - x.count || agentLabel(x.tag).localeCompare(agentLabel(y.tag)));
  return { agents, total: sessions.length, met, flagged };
}

// Angles are degrees clockwise from twelve o'clock.
const RAD = Math.PI / 180;
const pointAt = (cx, cy, r, deg) => [cx + r * Math.sin(deg * RAD), cy - r * Math.cos(deg * RAD)];
const degOf = (cx, cy, x, y) => {
  const d = Math.atan2(x - cx, cy - y) / RAD;
  return d < 0 ? d + 360 : d;
};
const fx = (n) => Math.round(n * 100) / 100;

// The ring between radii `ri` and `ro` from `a0` to `a1` degrees, as a path.
// More than half a turn is drawn as two halves, so an arc is never lost as
// one that ends where it starts; a whole turn (one agent, one kind of work)
// as its two edges, with no seam across it.
export function sectorPath(cx, cy, ri, ro, a0, a1) {
  const span = a1 - a0;
  if (!(span > 0)) return "";
  if (span >= 359.999) {
    const circle = (r, sweep) => `M${fx(cx)},${fx(cy - r)}A${fx(r)},${fx(r)} 0 0 ${sweep} ${fx(cx)},${fx(cy + r)}A${fx(r)},${fx(r)} 0 0 ${sweep} ${fx(cx)},${fx(cy - r)}Z`;
    return circle(ro, 1) + circle(ri, 0);
  }
  const stops = span > 180 ? [a0, a0 + span / 2, a1] : [a0, a1];
  const [x0, y0] = pointAt(cx, cy, ro, a0);
  let d = `M${fx(x0)},${fx(y0)}`;
  for (let i = 1; i < stops.length; i += 1) {
    const [x, y] = pointAt(cx, cy, ro, stops[i]);
    d += `A${fx(ro)},${fx(ro)} 0 0 1 ${fx(x)},${fx(y)}`;
  }
  const [xi, yi] = pointAt(cx, cy, ri, a1);
  d += `L${fx(xi)},${fx(yi)}`;
  for (let i = stops.length - 2; i >= 0; i -= 1) {
    const [x, y] = pointAt(cx, cy, ri, stops[i]);
    d += `A${fx(ri)},${fx(ri)} 0 0 0 ${fx(x)},${fx(y)}`;
  }
  return `${d}Z`;
}

// The rings' radii for a rim `R`: the centre's hole, the agents' ring, the
// ring outside it, and the rim's band.
function radiiOf(R) {
  const band = Math.max(6, Math.min(12, Math.round(R * 0.05)));
  const r2 = R - band - RING_GAP;
  const r0 = Math.round(Math.max(R * 0.33, Math.min(HOLE_MIN, R * 0.45)));
  const r1 = r0 + Math.round((r2 - r0 - RING_GAP) * 0.5);
  return { r0, r1, m0: r1 + RING_GAP, r2, b0: R - band };
}

// Whether a box `w` by `h` centred at (x, y) lies within the ring between
// `ri` and `ro` from `a0` to `a1`, a few px clear of its edges: its edges
// are tried point by point, as the ring's inner edge bows into a box.
function fitsIn(cx, cy, ri, ro, a0, a1, x, y, w, h) {
  const whole = a1 - a0 >= 359.999;
  for (let i = 0; i <= 8; i += 1) {
    const t = i / 8;
    const pts = [[x - w / 2 + t * w, y - h / 2], [x - w / 2 + t * w, y + h / 2], [x - w / 2, y - h / 2 + t * h], [x + w / 2, y - h / 2 + t * h]];
    for (const [px, py] of pts) {
      const r = Math.hypot(px - cx, py - cy);
      if (r < ri + 3 || r > ro - 3) return false;
      if (whole) continue;
      let d = degOf(cx, cy, px, py);
      if (d < a0) d += 360;
      if ((d - a0) * RAD * r < 4 || (a1 - d) * RAD * r < 4) return false;
    }
  }
  return true;
}

// Where an agent's name goes inside its arc: with its count under it, else
// alone; at the arc's middle or as near it as it fits (text sits best where
// the ring runs level, at the top and the foot). Null where it fits nowhere.
function insideOf(agent, geo) {
  const { cx, cy, r0, r1 } = geo;
  const span = agent.a1 - agent.a0;
  const rm = (r0 + r1) / 2;
  const tries = [];
  for (let k = 0; k <= 9; k += 1) for (const s of k ? [-1, 1] : [1]) tries.push(agent.a0 + span / 2 + (s * k * span) / 20);
  const nameW = textW(agent.name, 12) + 2;
  const countW = textW(NUM.format(agent.count), 11) + 2;
  for (const [w, h, lines] of [[Math.max(nameW, countW), NAME_H + STATS_H, 2], [nameW, NAME_H, 1]]) {
    for (const deg of tries) {
      const [x, y] = pointAt(cx, cy, rm, deg);
      if (fitsIn(cx, cy, r0, r1, agent.a0, agent.a1, x, y, w, h)) return { x, y, w, h, lines };
    }
  }
  return null;
}

// About how wide a name is on two lines, broken between words where that
// leaves the longer line shortest.
function twoLinesW(name) {
  const words = String(name).split(/\s+/).filter(Boolean);
  let best = textW(name, 12);
  for (let k = 1; k < words.length; k += 1) best = Math.min(best, Math.max(textW(words.slice(0, k).join(" "), 12), textW(words.slice(k).join(" "), 12)));
  return best;
}

// What a label says under its name, part by part, with about how wide each
// is: its sessions, and how many met a standard and were flagged.
const STATS_GAP = 6;
export function statsOf(s, reviewed) {
  const parts = [{ id: "count", text: NUM.format(s.count) }];
  if (reviewed && s.met) parts.push({ id: "met", text: `${NUM.format(s.met)} met` });
  if (reviewed && s.flagged) parts.push({ id: "flagged", text: `${NUM.format(s.flagged)} flagged` });
  return parts.map((p) => ({ ...p, w: textW(p.text) }));
}
const partsW = (parts) => parts.reduce((a, p) => a + p.w, 0) + STATS_GAP * Math.max(0, parts.length - 1);
// The parts that fit `w`: all of them where they do, else without the
// count, then without the flagged, so what met a standard goes last.
export function statsFit(parts, w) {
  let shown = parts;
  for (const drop of ["count", "flagged"]) {
    if (partsW(shown) <= w || shown.length === 1) break;
    shown = shown.filter((p) => p.id !== drop);
  }
  return shown;
}

// Segments and boxes, for labels that must not meet.
const orient = (a, b, c) => Math.sign((b[0] - a[0]) * (c[1] - a[1]) - (b[1] - a[1]) * (c[0] - a[0]));
const segsCross = (p, q, r, s) => {
  const o1 = orient(p, q, r);
  const o2 = orient(p, q, s);
  const o3 = orient(r, s, p);
  const o4 = orient(r, s, q);
  return o1 !== 0 && o2 !== 0 && o3 !== 0 && o4 !== 0 && o1 !== o2 && o3 !== o4;
};
const segDist = ([cx, cy], [px, py], [qx, qy]) => {
  const dx = qx - px;
  const dy = qy - py;
  const len = dx * dx + dy * dy;
  const t = len ? Math.max(0, Math.min(1, ((cx - px) * dx + (cy - py) * dy) / len)) : 0;
  return Math.hypot(cx - (px + t * dx), cy - (py + t * dy));
};
export const boxDist = ([cx, cy], b) => Math.hypot(cx - Math.min(Math.max(cx, b.x0), b.x1), cy - Math.min(Math.max(cy, b.y0), b.y1));
export const boxesMeet = (a, b, m = 0) => a.x0 < b.x1 + m && b.x0 < a.x1 + m && a.y0 < b.y1 + m && b.y0 < a.y1 + m;
const segHitsBox = (p, q, b) => {
  const inside = ([x, y]) => x > b.x0 && x < b.x1 && y > b.y0 && y < b.y1;
  if (inside(p) || inside(q)) return true;
  const c = [[b.x0, b.y0], [b.x1, b.y0], [b.x1, b.y1], [b.x0, b.y1]];
  return c.some((k, i) => segsCross(p, q, k, c[(i + 1) % 4]));
};
const legs = (l) => [[l.leader[0], l.leader[1]], [l.leader[1], l.leader[2]]];
export const leadersCross = (a, b) => legs(a).some(([p, q]) => legs(b).some(([r, s]) => segsCross(p, q, r, s)));
export const leaderHits = (l, box) => legs(l).some(([p, q]) => segHitsBox(p, q, box));

// Where a label at `y` (its first line's middle), `h` tall, on its side (1
// right, -1 left) starts or ends: as close in as keeps all of it clear of
// the rim.
function edgeAt(y, h, side, geo) {
  const { cx, cy, rim } = geo;
  const y0 = y - NAME_H / 2;
  const near = Math.min(Math.max(cy, y0), y0 + h) - cy;
  const half = Math.abs(near) < rim + CLEAR ? Math.sqrt((rim + CLEAR) ** 2 - near * near) : 0;
  return cx + side * (half + LABEL_OFF);
}
const roomAt = (y, h, side, geo) => {
  const edge = edgeAt(y, h, side, geo);
  return side > 0 ? geo.width - edge : edge;
};

// A label at `y`: its box, as wide as what it says or the room allows, and
// its leader from the rim out along its arc's middle, then across to it.
function labelAt(c, y, geo) {
  const { cx, cy, rim } = geo;
  const edge = edgeAt(y, c.h, c.side, geo);
  const room = c.side > 0 ? geo.width - edge : edge;
  const w = Math.max(0, Math.min(c.cw, LABEL_MAX, room));
  const y0 = y - NAME_H / 2;
  const box = c.side > 0 ? { x0: edge, x1: edge + w, y0, y1: y0 + c.h } : { x0: edge - w, x1: edge, y0, y1: y0 + c.h };
  const leader = [pointAt(cx, cy, rim + LEAD_FROM, c.deg), pointAt(cx, cy, rim + ELBOW, c.deg), [edge - c.side * 4, y]];
  return { ...c, y, box, w, leader };
}

// The nearest height to its arc where a label has room beside the rim (by
// the rim's widest it may have none): room to say all it says on a line
// each a little way from its arc, else room for what it needs at the least
// with its name on two lines, as far as a label may stand from its arc;
// within the room. Its height and lines there, or null where there is none.
function roomyAt(c, geo) {
  const tries = [[Math.min(c.cw, LABEL_MAX), geo.maxShift * 0.5, 1], [c.need, geo.maxShift, 2]];
  for (const [need, reach, lines] of tries) {
    const h = lines === 1 ? LABEL_H : LABEL_H2;
    const top = NAME_H / 2;
    const bottom = geo.chartH - h + NAME_H / 2;
    for (let d = 0; d <= reach; d += 2) {
      for (const y of d ? [c.ideal - d, c.ideal + d] : [c.ideal]) {
        if (y >= top && y <= bottom && roomAt(y, h, c.side, geo) >= need) {
          // A name that fits on one line there keeps to one.
          const one = lines === 1 || roomAt(y, LABEL_H, c.side, geo) >= c.nameW;
          return { at: y, lines: one ? 1 : 2, h: one ? LABEL_H : LABEL_H2 };
        }
      }
    }
  }
  return null;
}

// Labels down one side, in the order of their arcs, each as near where it
// wants to be as the others leave room for: those that would overlap are
// pushed apart as a group about where they all want to be, then kept within
// the room. Returns each one's top.
function stackSide(items, bottom) {
  const groups = [];
  for (const it of items) {
    groups.push({ items: [it], size: it.h, sum: it.at - NAME_H / 2, n: 1 });
    while (groups.length > 1) {
      const b = groups[groups.length - 1];
      const a = groups[groups.length - 2];
      if (a.sum / a.n + a.size + LABEL_GAP <= b.sum / b.n) break;
      const shift = a.size + LABEL_GAP;
      a.sum += b.sum - shift * b.n;
      a.n += b.n;
      a.size += LABEL_GAP + b.size;
      a.items.push(...b.items);
      groups.pop();
    }
  }
  const tops = [];
  for (const g of groups) {
    // Each item's offset in its group was taken off its wish, so the group's
    // top is their mean.
    let t = g.sum / g.n;
    for (const it of g.items) {
      tops.push({ it, t });
      t += it.h + LABEL_GAP;
    }
  }
  let lo = 0;
  for (const o of tops) {
    o.t = Math.max(o.t, lo);
    lo = o.t + o.it.h + LABEL_GAP;
  }
  let hi = bottom;
  for (let i = tops.length - 1; i >= 0; i -= 1) {
    tops[i].t = Math.min(tops[i].t, hi - tops[i].it.h);
    hi = tops[i].t - LABEL_GAP;
  }
  return tops;
}

// What is wrong with labels as placed, as the label to leave out for it: one
// out of the room, too narrow for what it must say, pushed too far from its
// arc or with a leader across the rings gives up the least of those crowding
// it; of two that meet, or whose leaders cross or run through the other, the
// lesser.
function misplaced(placed, geo) {
  const { cx, cy, rim, chartH, maxShift } = geo;
  const lesser = (a, b) => (a.prio > b.prio ? a : b);
  const crowd = (l) => placed.filter((o) => o.side === l.side && Math.abs(o.at - l.at) <= maxShift + LABEL_H2).reduce(lesser, l);
  for (const l of placed) {
    if (l.box.y0 < -0.5 || l.box.y1 > chartH + 0.5) return crowd(l);
    if (l.w < l.need - 0.5) return crowd(l);
    if (Math.abs(l.y - l.ideal) > maxShift) return crowd(l);
    if (segDist([cx, cy], l.leader[1], l.leader[2]) < rim + 1) return crowd(l);
    if (boxDist([cx, cy], l.box) < rim + CLEAR - 0.5) return crowd(l);
  }
  for (let i = 0; i < placed.length; i += 1) {
    for (let j = i + 1; j < placed.length; j += 1) {
      const a = placed[i];
      const b = placed[j];
      if (boxesMeet(a.box, b.box, 2) || leadersCross(a, b) || leaderHits(a, b.box) || leaderHits(b, a.box)) return lesser(a, b);
    }
  }
  return null;
}

// The labels outside the rim, `cands` in the order they matter: those with
// room near their arcs, as many as the room holds and each side's height
// does, laid out; then the least of what goes wrong left out until nothing
// does.
function placeLabels(cands, geo) {
  const most = labelsFor(geo.chartH);
  const used = new Map();
  let kept = [];
  for (const c of cands) {
    if (kept.length >= most) break;
    const spot = roomyAt(c, geo);
    if (!spot || (used.get(c.side) || 0) + spot.h + LABEL_GAP > geo.chartH + LABEL_GAP) continue;
    used.set(c.side, (used.get(c.side) || 0) + spot.h + LABEL_GAP);
    kept.push({ ...c, ...spot });
  }
  for (let guard = 0; guard <= 2 * cands.length; guard += 1) {
    const placed = [];
    for (const side of [1, -1]) {
      const items = kept.filter((c) => c.side === side).sort((a, b) => a.ideal - b.ideal || a.prio - b.prio);
      for (const { it, t } of stackSide(items, geo.chartH)) placed.push(labelAt(it, t + NAME_H / 2, geo));
    }
    // A name pushed to where one line no longer holds it takes a second,
    // and the side is laid out again.
    const cut = new Set(placed.filter((l) => l.lines === 1 && l.w < l.nameW - 1).map((l) => l.key));
    if (cut.size) {
      kept = kept.map((c) => (cut.has(c.key) ? { ...c, lines: 2, h: LABEL_H2 } : c));
      continue;
    }
    const out = misplaced(placed, geo);
    if (!out) return placed.sort((a, b) => a.prio - b.prio);
    kept = kept.filter((c) => c.key !== out.key);
  }
  return [];
}

// The picture for the room: the rim as large as the height allows, with room
// for labels beside it; the agents round the inner ring from twelve o'clock,
// the busiest first; each one's kinds of work (or models) round the ring
// outside it in the same span, the most sessions first, those too thin to
// see folded into one arc, and one for those not read yet last; under each,
// where review left its sessions on the rim; the agents' names inside their
// arcs where they fit; and the labels outside: the agents named nowhere
// else, every arc a standard was met in, then the biggest arcs.
export function ringsLayout(stats, { width, height, middle = "work", reviewed = true }) {
  const { agents, total } = stats;
  if (!total || !(width > 0) || !(height > 0)) return null;
  const chartH = Math.max(0, height - CAP_H);
  const labelRoom = Math.max(80, Math.min(190, width * 0.13));
  const R = Math.floor(Math.max(48, Math.min(chartH / 2 - SPIKE - 2, width / 2 - SPIKE - CLEAR - LABEL_OFF - labelRoom)));
  const geo = { cx: width / 2, cy: chartH / 2, R, rim: R + SPIKE, ...radiiOf(R), width, chartH, total, maxShift: shiftOf(R) };
  const { cx, cy, r0, r1, m0, r2, b0 } = geo;
  const many = (MIDDLES.find((m) => m.id === middle) || MIDDLES[0]).many;
  const unit = 360 / total;
  const shapes = new Map();
  const arcs = [];
  const bands = [];
  let deg = 0;
  const rings = agents.map((a) => {
    const a0 = deg;
    const a1 = deg + a.count * unit;
    deg = a1;
    // Its name as known now, to size what says it; what is drawn reads it
    // again, as an agent's name can arrive after its sessions.
    const ring = { key: `agent:${a.tag}`, kind: "agent", tag: a.tag, name: agentLabel(a.tag), count: a.count, met: a.met, flagged: a.flagged, a0, a1, d: sectorPath(cx, cy, r0, r1, a0, a1), arcs: [] };
    ring.inside = insideOf(ring, geo);
    shapes.set(ring.key, ring);
    // Its values, the most sessions first; those too thin to see, where
    // there are two or more, folded into one arc after them.
    const values = [...a[middle]].filter(([v]) => v !== "").sort(byCount);
    const thin = values.filter(([, e]) => e.count * unit < FOLD_DEG);
    const folded = thin.length > 1 ? thin : [];
    const parts = values.slice(0, values.length - folded.length).map(([v, e], i) => ({ key: `arc:${a.tag}:${v}`, kind: "arc", value: v, ...e, rank: i }));
    if (folded.length) {
      const sum = (k) => folded.reduce((acc, [, e]) => acc + e[k], 0);
      parts.push({ key: `fold:${a.tag}`, kind: "fold", value: null, count: sum("count"), met: sum("met"), flagged: sum("flagged"), folded: folded.map(([v, e]) => [v, e.count]), alone: folded.length === values.length });
    }
    const unread = a[middle].get("");
    if (unread) parts.push({ key: `unread:${a.tag}`, kind: "unread", value: "", ...unread });
    let at = a0;
    for (const p of parts) {
      const arc = { ...p, tag: a.tag, agentKey: ring.key, a0: at, a1: at + p.count * unit };
      at = arc.a1;
      arc.d = sectorPath(cx, cy, m0, r2, arc.a0, arc.a1);
      arc.shade = arc.kind === "arc" ? STEP_P[Math.min(arc.rank, STEP_P.length - 1)] : arc.kind === "fold" ? FOLD_P : UNREAD_P;
      // Where review left its sessions, on the rim under it: the share that
      // met a standard, then the share flagged, then nothing.
      if (reviewed && (arc.met || arc.flagged)) {
        const span = arc.a1 - arc.a0;
        const metTo = arc.a0 + (span * arc.met) / arc.count;
        const flagTo = metTo + (span * arc.flagged) / arc.count;
        // The share that met a standard stands out past the rim, with a
        // fainter halo round it, so the thinnest of them still catches the
        // eye; its length round the rim stays its true share.
        if (arc.met) {
          const len = (metTo - arc.a0) * RAD * R;
          const pad = Math.max(3, (HALO_MIN - len) / 2) / (RAD * R);
          bands.push({ key: arc.key, place: "met", a0: arc.a0, a1: metTo, d: sectorPath(cx, cy, b0, R + SPIKE, arc.a0, metTo), halo: sectorPath(cx, cy, b0, R + SPIKE, arc.a0 - pad, metTo + pad) });
        }
        if (arc.flagged) bands.push({ key: arc.key, place: "flagged", a0: metTo, a1: flagTo, d: sectorPath(cx, cy, b0, R, metTo, flagTo) });
      }
      ring.arcs.push(arc);
      arcs.push(arc);
      shapes.set(arc.key, arc);
    }
    return ring;
  });
  // The labels outside the rim, in the order they matter: the agents named
  // nowhere else, every arc a standard was met in, then the biggest.
  const order = [];
  const seen = new Set();
  const add = (s) => {
    if (seen.has(s.key)) return;
    seen.add(s.key);
    order.push(s);
  };
  rings.filter((r) => !r.inside).forEach(add);
  if (reviewed) arcs.filter((x) => x.met && x.kind !== "unread").sort((x, y) => y.met - x.met || y.count - x.count).forEach(add);
  arcs.filter((x) => x.kind === "arc").sort((x, y) => y.count - x.count).slice(0, BIGGEST).forEach(add);
  const cands = order.map((s, prio) => {
    const mid = (s.a0 + s.a1) / 2;
    const name = s.kind === "fold" ? foldName(s, many) : s.kind === "agent" ? s.name : s.value;
    const parts = statsOf(s, reviewed);
    const nameW = SWATCH_W + textW(name, 12) + 2;
    const cw = Math.max(nameW, partsW(parts) + 2);
    // At the least, room for the part of what it says that goes last, and
    // for its name on two lines.
    const need = Math.max(partsW(statsFit(parts, 0)) + 2, Math.min(cw, Math.max(LABEL_MIN, SWATCH_W + twoLinesW(name) + 6), LABEL_MAX));
    return { key: s.key, prio, deg: mid, side: Math.sin(mid * RAD) >= 0 ? 1 : -1, ideal: cy - (geo.rim + ELBOW) * Math.cos(mid * RAD), cw, nameW, need, name, parts };
  });
  const labels = placeLabels(cands, geo).map((l) => ({ ...l, d: `M${l.leader.map(([x, y]) => `${fx(x)},${fx(y)}`).join("L")}` }));
  return { ...geo, agents: rings, arcs, bands, labels, shapes };
}

// The caption over the picture, in the caption's words: what the rings are
// (the ring outside the agents a switch), what the rim's colours say, and
// each agent's colour, as many of those as the line holds, in that order;
// an agent the picture names nowhere comes before what an arc's size is.
function legendOf(width, geo, reviewed) {
  const dot = 20 + capW("\u00b7");
  let used = capW("Rings: agents, then") + 10 + capW("Work") + 10 + capW("Models");
  const rimW = dot + capW("Rim:") + 8 + (8 + 4 + capW("Standard met")) + 10 + (8 + 4 + capW("Flagged"));
  const rim = reviewed && used + rimW <= width;
  if (rim) used += rimW;
  const keysW = (list) => 24 + list.reduce((acc, a) => acc + 12 + 8 + 4 + capW(a.name), 0);
  const named = new Set(geo.labels.map((l) => l.key));
  const unnamed = geo.agents.filter((a) => !a.inside && !named.has(a.key));
  const sizeW = dot + capW("Size: traces");
  if (used + sizeW + keysW(geo.agents) <= width) return { rim, size: true, keys: geo.agents };
  const keys = unnamed.length && used + keysW(unnamed) <= width ? unnamed : [];
  if (keys.length) used += keysW(keys);
  return { rim, size: used + sizeW <= width, keys };
}

// The ring's switch, inside the caption: its choices side by side, the one
// shown as the caption is, the other faint until pointed at.
function MiddleSwitch({ middle, onSwap }) {
  return (
    <span className="flex items-baseline gap-[10px]" role="group" aria-label="Work or models" data-ring-switch="">
      {MIDDLES.map((m) => {
        const on = m.id === middle.id;
        return (
          <button
            key={m.id}
            type="button"
            aria-pressed={on}
            onClick={on ? undefined : () => onSwap(m.id)}
            className={`${EYEBROW_CLASS} transition-opacity ${on ? "cursor-default" : "opacity-[0.45] hover:opacity-100"}`}
            data-ring-switch-option={m.id}
          >
            {m.label}
          </button>
        );
      })}
    </span>
  );
}

const Swatch = ({ color }) => <span className="inline-block h-[8px] w-[8px] shrink-0 self-center rounded-[1px]" style={{ background: color }} aria-hidden="true" />;
const Sep = () => <span className={EYEBROW_CLASS} aria-hidden="true">{"\u00b7"}</span>;

function Legend({ width, geo, middle, onSwap, reviewed, hues }) {
  const shown = useMemo(() => legendOf(width, geo, reviewed), [width, geo, reviewed]);
  return (
    <div className="flex min-w-0 items-baseline gap-[10px] overflow-hidden whitespace-nowrap" style={{ width, height: CAP_H }} data-ring-legend="">
      <span className={EYEBROW_CLASS}>Rings: agents, then</span>
      <MiddleSwitch middle={middle} onSwap={onSwap} />
      {shown.rim ? (
        <>
          <Sep />
          <span className="flex items-baseline gap-[4px]" data-ring-legend-rim="">
            <span className={`${EYEBROW_CLASS} mr-[4px]`}>Rim:</span>
            <Swatch color={MET} />
            <span className={`${EYEBROW_CLASS} mr-[6px]`}>Standard met</span>
            <Swatch color={FLAGGED_BAND} />
            <span className={EYEBROW_CLASS}>Flagged</span>
          </span>
        </>
      ) : null}
      {shown.size ? (
        <>
          <Sep />
          <span className={EYEBROW_CLASS}>Size: traces</span>
        </>
      ) : null}
      {shown.keys.length ? (
        <span className="ml-auto flex items-baseline gap-[12px]" data-ring-legend-agents="">
          {shown.keys.map((a) => (
            <span key={a.key} className="flex items-baseline gap-[4px]">
              <Swatch color={hueOf(hues?.get(a.tag))} />
              <span className={EYEBROW_CLASS}>{agentLabel(a.tag)}</span>
            </span>
          ))}
        </span>
      ) : null}
    </div>
  );
}

// What a label says under its name: its sessions, then those that met a
// standard and those flagged, in review's colours, as many as its width
// holds.
const PART_COLOR = { count: "var(--muted)", met: MET, flagged: FLAGGED };
function Stats({ parts, right = true }) {
  return (
    <span className={`flex min-w-0 items-baseline gap-[6px] overflow-hidden whitespace-nowrap text-[11px] leading-[14px] tabular-nums ${right ? "" : "justify-end"}`} data-ring-label-stats="">
      {parts.map((p) => <span key={p.id} style={{ color: PART_COLOR[p.id] }}>{p.text}</span>)}
    </span>
  );
}

// A label outside the rim: the agent's colour and the arc's name (on two
// lines where the room beside the rim is short), and under it its sessions
// and what review left; right of the rings it reads from its leader out,
// left of them it ends at its leader.
function OutLabel({ label, shape, hue, faded }) {
  const right = label.side > 0;
  const two = label.lines > 1;
  return (
    <span
      className="absolute grid content-start transition-opacity duration-150"
      style={{ left: label.box.x0, top: label.box.y0, width: label.w, height: label.h, opacity: faded ? FADED : 1, cursor: "pointer" }}
      aria-hidden="true"
      data-ring-key={label.key}
      data-ring-label={label.key}
    >
      {/* The agent's colour next to the leader: before the name on the right,
          after it on the left. */}
      <span className={`flex min-w-0 items-start gap-[6px] leading-[16px] ${right ? "" : "flex-row-reverse"}`}>
        <span className="mt-[4px] h-[8px] w-[8px] shrink-0 rounded-[1px]" style={{ background: hueOf(hue) }} aria-hidden="true" />
        <span className={`min-w-0 text-[12px] ${two ? "line-clamp-2 break-words" : "truncate"} ${right ? "" : "text-right"} ${shape.kind === "fold" ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{shape.kind === "agent" ? agentLabel(shape.tag) : label.name}</span>
      </span>
      <Stats parts={statsFit(label.parts, label.w - 2)} right={right} />
    </span>
  );
}

// An agent's name inside its arc, with its count under it where that fits.
function InLabel({ agent, faded }) {
  const { x, y, w, h, lines } = agent.inside;
  return (
    <span
      className="absolute grid content-center justify-items-center transition-opacity duration-150"
      style={{ left: x - w / 2, top: y - h / 2, width: w, height: h, opacity: faded ? FADED : 1, cursor: agent.tag ? "pointer" : "default" }}
      aria-hidden="true"
      data-ring-key={agent.key}
      data-ring-inside={agent.tag}
    >
      <span className="whitespace-nowrap text-[12px] leading-[16px] text-[color:var(--ink)]">{agentLabel(agent.tag)}</span>
      {lines > 1 ? <span className="whitespace-nowrap text-[11px] leading-[14px] tabular-nums text-[color:var(--muted)]">{NUM.format(agent.count)}</span> : null}
    </span>
  );
}

// A review count in the centre: how many, in review's colour (none in the
// faint one), a click narrowing the search to them; past the sample's cap,
// how many of all the search reaches, on a line under it.
function CentreCount({ n, label, color, clause, query, onPick, inAll }) {
  const said = <span className="block whitespace-nowrap text-[11px] leading-[14px] tabular-nums" style={{ color: n ? color : "var(--muted-soft)" }}>{`${NUM.format(n)} ${label}`}</span>;
  return (
    <>
      {n ? (
        <Drill clause={clause} query={query} onPick={onPick} className="pointer-events-auto rounded-[2px] p-[3px]" data-ring-centre-count={label}>{said}</Drill>
      ) : (
        <span className="p-[3px]" data-ring-centre-count={label}>{said}</span>
      )}
      {inAll != null && inAll !== n ? <span className="whitespace-nowrap text-[11px] leading-[14px] tabular-nums text-[color:var(--muted-soft)]" data-ring-in-all={label}>{`${NUM.format(inAll)} in all`}</span> : null}
    </>
  );
}

// What a shape is called: an agent, a kind of work (or model), the folded
// rest, or the sessions whose kind of work is not read yet.
function nameOf(shape, middle, workRead) {
  if (shape.kind === "agent") return agentLabel(shape.tag);
  if (shape.kind === "fold") return foldName(shape, middle.many);
  if (shape.kind === "unread") return middle.id === "models" ? "No model recorded" : workRead === "failed" ? "Kind of work could not be read" : "Kind of work not read yet";
  return shape.value;
}

// What the tooltip says of a shape: its name (and its agent's), its
// sessions and their share, where review left them, and for a folded arc
// the first of what it folds.
function tipOf(shape, geo, { middle, reviewed, capped, workRead }) {
  const agent = shape.kind === "agent" ? null : geo.shapes.get(shape.agentKey);
  const rest = shape.kind === "fold" ? shape.folded.slice(0, 4).map(([v]) => v).join(", ") + (shape.folded.length > 4 ? ` and ${NUM.format(shape.folded.length - 4)} more` : "") : "";
  return (
    <span className="grid gap-[2px]" data-ring-tip="">
      <span className="text-[12px] leading-[16px] text-[color:var(--ink)]">{nameOf(shape, middle, workRead)}</span>
      {agent ? <span className="text-[color:var(--muted)]">{`with ${agentLabel(agent.tag)}`}</span> : null}
      <span className="tabular-nums text-[color:var(--muted)]">{`${plural(shape.count, "trace", "traces")}, ${pctOf(shape.count, geo.total)} of ${capped ? "these" : "all"}`}</span>
      {reviewed ? (
        shape.met || shape.flagged ? (
          <span className="flex gap-[8px] tabular-nums">
            {shape.met ? <span style={{ color: MET }}>{`${NUM.format(shape.met)} met a standard`}</span> : null}
            {shape.flagged ? <span style={{ color: FLAGGED }}>{`${NUM.format(shape.flagged)} flagged`}</span> : null}
          </span>
        ) : <span className="text-[color:var(--muted-soft)]">Nothing flagged</span>
      ) : null}
      {rest ? <span className="text-[color:var(--muted-soft)]">{rest}</span> : null}
    </span>
  );
}

// What the finder points at, as the shapes it names.
function litOf(preview, geo, middle) {
  if (!preview || !geo) return null;
  if (preview.def === STEPS.agent) return geo.shapes.has(`agent:${preview.value}`) ? new Set([`agent:${preview.value}`]) : null;
  const named = (middle.id === "work" && preview.def === STEPS.topic) || (middle.id === "models" && !!MODEL_OF && preview.of === MODEL_OF);
  if (!named) return null;
  const keys = new Set(geo.arcs.filter((a) => a.kind === "arc" && a.value === preview.value).map((a) => a.key));
  return keys.size ? keys : null;
}

const keyOf = (e) => e.target?.closest?.("[data-ring-key]")?.getAttribute("data-ring-key") || null;

export default function RingsView({ facts, reached = null, width, height, query, onPick, onPickAll, hues, preview = null, onShowAll }) {
  const [middleId, setMiddleId] = useState(readMiddle);
  const middle = MIDDLES.find((m) => m.id === middleId) || MIDDLES[0];
  const onSwap = useCallback((id) => {
    setMiddleId(id);
    keepMiddle(id);
  }, []);
  const reviewed = facts.reviewed !== false;
  const stats = useMemo(() => ringsOf(facts.sessions), [facts.sessions]);
  const geo = useMemo(() => ringsLayout(stats, { width, height, middle: middleId, reviewed }), [stats, width, height, middleId, reviewed]);
  const svgRef = useRef(null);
  // The shape pointed at (or focused), and where its tooltip stands.
  const [tip, setTip] = useState(null);
  const point = useCallback((key) => {
    const shape = key ? geo?.shapes.get(key) : null;
    const svg = svgRef.current;
    if (!shape || !svg) {
      setTip(null);
      return;
    }
    setTip((cur) => {
      if (cur && cur.key === key) return cur;
      // Anchored on the rim at the shape's middle, out of the rings' way.
      const mid = (shape.a0 + shape.a1) / 2;
      const [x, y] = pointAt(geo.cx, geo.cy, geo.rim + 4, mid);
      const box = svg.getBoundingClientRect();
      const left = box.left + x;
      const top = box.top + y;
      const c = Math.cos(mid * RAD);
      const placement = c > 0.7 ? "top" : c < -0.7 ? "bottom" : Math.sin(mid * RAD) >= 0 ? "right" : "left";
      return { key, placement, rect: { left, top, right: left + 1, bottom: top + 1, width: 1, height: 1, x: left, y: top } };
    });
  }, [geo]);
  const activate = useCallback((key) => {
    const shape = geo?.shapes.get(key);
    if (!shape) return;
    if (shape.kind === "agent") {
      if (shape.tag) onPick(STEPS.agent.clause(shape.tag));
    } else if (shape.kind === "fold") {
      onShowAll?.(middle.part);
    } else if (shape.kind === "arc" && shape.tag) {
      onPickAll([STEPS.agent.clause(shape.tag), middle.clause(shape.value)]);
    }
  }, [geo, onPick, onPickAll, onShowAll, middle]);
  const lit = useMemo(() => litOf(preview, geo, middle), [preview, geo, middle]);
  if (!facts.sessions.length) return <BodyState facts={facts} />;
  if (!geo) return null;
  const capped = !!facts.capped;
  const workRead = facts.topics;
  // What stands forward while something is pointed at (or named in the
  // finder): it, with its agent or with its arcs; the rest stands back.
  const on = tip ? geo.shapes.get(tip.key) || null : null;
  const lifted = on ? new Set([on.key]) : lit;
  let forward = null;
  if (lifted) {
    forward = new Set();
    for (const k of lifted) {
      const s = geo.shapes.get(k);
      forward.add(k);
      if (s?.kind === "agent") for (const a of s.arcs) forward.add(a.key);
      else if (s) forward.add(s.agentKey);
    }
  }
  const faded = (key) => !!forward && !forward.has(key);
  const heldAgent = (a) => !!a.tag && holdsClause(query, STEPS.agent.clause(a.tag));
  const heldArc = (a) => a.kind === "arc" && !!a.tag && holdsClause(query, STEPS.agent.clause(a.tag)) && holdsClause(query, middle.clause(a.value));
  const reachedFlagged = reached ? (reached.not_verified || 0) + (reached.not_met || 0) + (reached.set_aside || 0) : null;
  const ariaOf = (s) => {
    const review = reviewed ? `, ${NUM.format(s.met)} met a standard, ${NUM.format(s.flagged)} flagged` : "";
    const who = s.kind === "agent" ? "" : ` with ${agentLabel(s.tag)}`;
    return `${nameOf(s, middle, workRead)}${who}: ${plural(s.count, "trace", "traces")}${review}`;
  };
  const onKeyDown = (e) => {
    const key = keyOf(e);
    if (!key || (e.key !== "Enter" && e.key !== " ")) return;
    e.preventDefault();
    activate(key);
  };
  const shapeStyle = (fill, fade, button) => ({ fill, opacity: fade ? FADED : 1, stroke: "var(--workspace-bg)", strokeWidth: 1, transition: "fill 150ms, opacity 150ms", outline: "none", cursor: button ? "pointer" : "default" });
  return (
    <div className="relative" style={{ width, height }} data-rings-view="" data-ring-middle={middle.id}>
      <Legend width={width} geo={geo} middle={middle} onSwap={onSwap} reviewed={reviewed} hues={hues} />
      <div
        className="absolute left-0"
        style={{ top: CAP_H, width, height: geo.chartH }}
        onPointerOver={(e) => point(keyOf(e))}
        onPointerLeave={() => setTip(null)}
        onFocus={(e) => point(keyOf(e))}
        onBlur={() => setTip(null)}
        onClick={(e) => {
          const key = keyOf(e);
          if (key) activate(key);
        }}
        onKeyDown={onKeyDown}
        data-ring-chart=""
      >
        <svg ref={svgRef} className="absolute left-0 top-0 block overflow-visible" width={width} height={geo.chartH} role="group" aria-label={`Traces by agent and ${middle.one}`}>
          <g data-ring-layer="agents">
            {geo.agents.map((a) => (
              <g
                key={a.key}
                role={a.tag ? "button" : undefined}
                tabIndex={a.tag ? 0 : undefined}
                aria-pressed={a.tag ? heldAgent(a) : undefined}
                aria-label={ariaOf(a)}
                style={{ outline: "none" }}
                data-ring-key={a.key}
                data-ring-agent={a.tag}
                data-a0={a.a0}
                data-a1={a.a1}
              >
                <path d={a.d} style={shapeStyle(grey(lifted?.has(a.key) ? AGENT_LIT_P : AGENT_P), faded(a.key), !!a.tag)} />
              </g>
            ))}
          </g>
          <g data-ring-layer="edges" aria-hidden="true" style={{ pointerEvents: "none" }}>
            {geo.agents.map((a) => <path key={a.key} d={sectorPath(geo.cx, geo.cy, geo.r0, geo.r0 + EDGE_W, a.a0, a.a1)} style={{ fill: hueOf(hues?.get(a.tag)), opacity: faded(a.key) ? FADED : 1, transition: "opacity 150ms" }} data-ring-edge={a.tag} />)}
          </g>
          <g data-ring-layer="arcs">
            {geo.arcs.map((a) => {
              const button = a.kind === "fold" || (a.kind === "arc" && !!a.tag);
              return (
                <g
                  key={a.key}
                  role={button ? "button" : undefined}
                  tabIndex={button ? 0 : undefined}
                  aria-pressed={a.kind === "arc" && a.tag ? heldArc(a) : undefined}
                  aria-label={ariaOf(a)}
                  style={{ outline: "none" }}
                  data-ring-key={a.key}
                  data-ring-arc={a.kind === "arc" ? `${a.tag}:${a.value}` : undefined}
                  data-ring-fold={a.kind === "fold" ? a.tag : undefined}
                  data-ring-unread={a.kind === "unread" ? a.tag : undefined}
                  data-a0={a.a0}
                  data-a1={a.a1}
                >
                  <path d={a.d} style={shapeStyle(grey(a.shade + (lifted?.has(a.key) ? LIFT_P : 0)), faded(a.key), button)} />
                </g>
              );
            })}
          </g>
          {reviewed ? (
            <g data-ring-layer="rim">
              {geo.bands.map((b) => (b.halo ? <path key={`${b.key}:halo`} d={b.halo} style={{ fill: MET, opacity: faded(b.key) ? 0.12 : 0.3, transition: "opacity 150ms" }} data-ring-key={b.key} aria-hidden="true" /> : null))}
              {geo.bands.map((b) => (
                <path
                  key={`${b.key}:${b.place}`}
                  d={b.d}
                  // A hairline of its own colour round the share that met a
                  // standard, so the thinnest is never thinner than a line.
                  style={{ fill: b.place === "met" ? MET : FLAGGED_BAND, stroke: b.place === "met" ? MET : "none", strokeWidth: 1.25, opacity: faded(b.key) ? FADED : 1, transition: "opacity 150ms" }}
                  data-ring-key={b.key}
                  data-ring-band={b.place}
                  data-ring-band-of={b.key}
                  data-a0={b.a0}
                  data-a1={b.a1}
                  aria-hidden="true"
                />
              ))}
            </g>
          ) : null}
          <g data-ring-layer="leaders" aria-hidden="true">
            {geo.labels.map((l) => (
              <path key={l.key} d={l.d} style={{ fill: "none", stroke: "var(--line-strong)", strokeWidth: 1, opacity: faded(l.key) ? FADED : 1, transition: "opacity 150ms" }} data-ring-leader={l.key} />
            ))}
          </g>
          {/* What the search is narrowed to, outlined over its neighbours. */}
          <g data-ring-layer="held" aria-hidden="true" style={{ pointerEvents: "none" }}>
            {geo.agents.filter(heldAgent).map((a) => <path key={a.key} d={a.d} style={{ fill: "none", stroke: "var(--ink)", strokeWidth: 1.5 }} data-ring-held={a.key} />)}
            {geo.arcs.filter(heldArc).map((a) => <path key={a.key} d={a.d} style={{ fill: "none", stroke: "var(--ink)", strokeWidth: 1.5 }} data-ring-held={a.key} />)}
          </g>
        </svg>
        {geo.agents.filter((a) => a.inside).map((a) => <InLabel key={a.key} agent={a} faded={faded(a.key)} />)}
        {geo.labels.map((l) => {
          const shape = geo.shapes.get(l.key);
          return <OutLabel key={l.key} label={l} shape={shape} hue={hues?.get(shape.tag)} faded={faded(l.key)} />;
        })}
        <div
          className="pointer-events-none absolute flex flex-col items-center justify-center"
          style={{ left: geo.cx - geo.r0, top: geo.cy - geo.r0, width: 2 * geo.r0, height: 2 * geo.r0 }}
          data-ring-centre=""
        >
          <span className="text-[22px] font-medium leading-[28px] tabular-nums text-[color:var(--ink)]" data-ring-total="">{NUM.format(stats.total)}</span>
          <span className="mb-[2px] whitespace-nowrap text-[11px] leading-[14px] text-[color:var(--muted)]">{capped ? "most recent traces" : stats.total === 1 ? "trace" : "traces"}</span>
          {reviewed ? (
            <>
              <CentreCount n={stats.met} label="met a standard" color={MET} clause={MET_CLAUSE} query={query} onPick={onPick} inAll={capped && reached ? reached.met ?? null : null} />
              <CentreCount n={stats.flagged} label="flagged" color={FLAGGED} clause={FLAGGED_CLAUSE} query={query} onPick={onPick} inAll={capped ? reachedFlagged : null} />
            </>
          ) : (
            <span className="mt-[3px]"><ReviewReading facts={facts} /></span>
          )}
        </div>
      </div>
      {on ? <HoverTooltip anchorRect={tip.rect} placement={tip.placement} wide label={tipOf(on, geo, { middle, reviewed, capped, workRead })} /> : null}
    </div>
  );
}
